/**
 * Eine Zeile der Feld-Tabelle in Wizard-Schritt 2: Feld-Checkbox, Rezept-Zelle
 * und "Als Standard merken". Abgewaehlte Felder zeigen statt der Rezept-Zelle
 * einen Platzhalter, damit die Spaltenflucht erhalten bleibt.
 */

import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/v2/ui'
import type { SelectOption } from '@/components/v2/ui'
import type { BulkAttributeCode } from '@/lib/api/channel-content-bulk'

import { RecipeSelectCell } from './RecipeSelectCell'
import type { FieldSelection } from './useFieldSelections'

interface ChannelFieldRowProps {
  channelId: string
  channelLabel: string
  code: BulkAttributeCode
  field: FieldSelection
  templateOptions: SelectOption[]
  /** GOKI-Standard-Tools (TOOL_DEFINITIONS) — exklusiv zu templateOptions, per Prefix unterschieden. */
  toolOptions?: SelectOption[]
  onFieldChange: (patch: Partial<FieldSelection>) => void
}

export function ChannelFieldRow({
  channelId,
  channelLabel,
  code,
  field,
  templateOptions,
  toolOptions = [],
  onFieldChange,
}: ChannelFieldRowProps) {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-[minmax(9rem,14rem)_1fr_minmax(8rem,12rem)] items-center gap-4 px-4 py-2">
      <Checkbox
        id={`text-bulk-wizard-channel-${channelId}-field-${code}-toggle`}
        checked={field.enabled}
        onChange={() => onFieldChange({ enabled: !field.enabled })}
        label={t(`channelContent.card.attribute.${code}`)}
      />

      {field.enabled ? (
        <RecipeSelectCell
          channelId={channelId}
          channelLabel={channelLabel}
          code={code}
          field={field}
          templateOptions={templateOptions}
          toolOptions={toolOptions}
          onFieldChange={onFieldChange}
        />
      ) : (
        <span
          aria-hidden="true"
          className="rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-400 dark:border-neutral-800 dark:text-neutral-600"
        >
          &mdash;
        </span>
      )}

      <Checkbox
        id={`text-bulk-wizard-channel-${channelId}-field-${code}-remember-toggle`}
        checked={field.rememberDefault}
        disabled={!field.enabled}
        onChange={() => onFieldChange({ rememberDefault: !field.rememberDefault })}
        label={
          field.rememberDefault
            ? t('channelContent.wizard.remembered')
            : t('channelContent.wizard.remember')
        }
      />
    </div>
  )
}
